import React, { useState } from 'react';
import Select from 'react-select';
import { Pencil, AlertTriangle } from 'lucide-react';

import { useStudentSearch } from '@/components/form/fields/student-search/useStudentSearch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface StudentSnapshot {
  student_id: string;
  student_full_name: string;
  student_nickname: string;
}

interface StudentEditFieldProps {
  currentValues: StudentSnapshot;
  original: StudentSnapshot;
  onChange: (next: StudentSnapshot) => void;
  mode?: 'admin' | 'teacher';
}

const selectStyles = {
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    minHeight: '40px',
    borderRadius: '0.375rem',
    borderColor: '#e2e8f0',
    boxShadow: 'none',
    '&:hover': { borderColor: '#cbd5e1' },
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided: Record<string, unknown>, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#2563eb' : state.isFocused ? '#e2e8f0' : undefined,
    color: state.isSelected ? 'white' : 'inherit',
    cursor: 'pointer',
  }),
};

export const StudentEditField: React.FC<StudentEditFieldProps> = ({
  currentValues,
  original,
  onChange,
  mode = 'admin',
}) => {
  const isTeacher = mode === 'teacher';
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { searchQuery, setSearchQuery, students, isLoading } = useStudentSearch(true, () => {});

  const isChanged = currentValues.student_id !== original.student_id;

  const handleRequestEdit = () => setShowConfirm(true);
  const handleConfirmEdit = () => {
    setShowConfirm(false);
    setIsEditMode(true);
  };
  const handleCancelEdit = () => {
    onChange(original);
    setSearchQuery('');
    setIsEditMode(false);
  };

  const options = students.map((s) => ({
    value: s.student_id,
    label: `${s.full_name} (${s.nickname}) - รหัส ${s.student_id}`,
    student: s,
  }));

  const currentOption = currentValues.student_id
    ? {
        value: currentValues.student_id,
        label: `${currentValues.student_full_name} (${currentValues.student_nickname}) - รหัส ${currentValues.student_id}`,
        student: null,
      }
    : null;

  return (
    <div className="space-y-3">
      {!isEditMode ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label className="mb-2 block">ชื่อ-นามสกุล</Label>
              <Input value={currentValues.student_full_name} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label className="mb-2 block">ชื่อเล่น</Label>
              <Input value={currentValues.student_nickname} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label className="mb-2 block">รหัสนักเรียน</Label>
              <Input value={currentValues.student_id} disabled className="bg-gray-50" />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRequestEdit}
              className="w-fit"
            >
              <Pencil className="h-4 w-4 mr-1" />
              แก้ไขข้อมูลนักเรียน
            </Button>
            {isChanged && (
              <span className="text-xs text-amber-600">
                {isTeacher
                  ? '* เลือกนักเรียนใหม่แล้ว — กดส่งคำขอ รอ admin อนุมัติ'
                  : '* เลือกนักเรียนใหม่แล้ว — กดบันทึกเพื่อยืนยันการเปลี่ยนแปลง'}
              </span>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50/40 p-4">
          <div className="flex items-start gap-2 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>
              {isTeacher
                ? 'กำลังขอเปลี่ยนนักเรียนของรายงานนี้ — การเปลี่ยนแปลงจะต้องรอ admin อนุมัติก่อน'
                : 'กำลังแก้ไขนักเรียนของรายงานนี้ — กรุณาตรวจสอบก่อนกดบันทึก'}
            </span>
          </div>
          <Label className="block text-sm font-medium">ค้นหานักเรียน (พิมพ์อย่างน้อย 3 ตัวอักษร)</Label>
          <Select
            options={options}
            value={currentOption}
            onChange={(opt) => {
              if (opt && opt.student) {
                onChange({
                  student_id: opt.student.student_id,
                  student_full_name: opt.student.full_name,
                  student_nickname: opt.student.nickname,
                });
              }
            }}
            onInputChange={(v) => setSearchQuery(v)}
            isLoading={isLoading}
            isClearable={false}
            placeholder="ค้นหาด้วยชื่อ / ชื่อเล่น / รหัส"
            noOptionsMessage={() =>
              searchQuery.length < 3 ? 'พิมพ์อย่างน้อย 3 ตัวอักษร' : 'ไม่พบนักเรียน'
            }
            styles={selectStyles}
            menuPosition="fixed"
          />
          <div className="text-xs text-gray-600">
            นักเรียนปัจจุบัน:{' '}
            <span className="font-medium">
              {currentValues.student_full_name} ({currentValues.student_nickname})
            </span>{' '}
            - รหัส <span className="font-mono">{currentValues.student_id}</span>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCancelEdit}
            >
              ยกเลิก กลับเป็นค่าเดิม
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ยืนยันการแก้ไขข้อมูลนักเรียน</AlertDialogTitle>
            <AlertDialogDescription>
              ข้อมูลนักเรียนเป็นข้อมูลสำคัญ — การเปลี่ยนนักเรียนของรายงานจะส่งผลต่อรายงาน,
              สถิติ และการหักชั่วโมงเรียน
              {isTeacher
                ? ' การเปลี่ยนแปลงนี้จะถูกส่งเป็นคำขอ รอ admin อนุมัติก่อนมีผล'
                : ''}{' '}
              คุณแน่ใจหรือไม่ว่าต้องการแก้ไข?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>ยกเลิก</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmEdit}>
              ยืนยัน เริ่มแก้ไข
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
